import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, Search, ChevronLeft, RefreshCw, User as UserIcon,
  Users, Zap, Eye, ListChecks, TrendingUp, Loader2,
} from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminReactivation — /admin/reactivation
 * Super-Admin uniquement. Recherche et statistiques sur les Talents souhaitant
 * réactiver une compétence. Additif : ne modifie aucun matching existant.
 */
export default function AdminReactivation() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    current_practice: "",
    wants_to_resume: "",
    since_last_practice: "",
    skill_q: "",
    to_reactivate_only: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const [list, st] = await Promise.all([
        api.get("/v1/reactivation/admin/list", { params }),
        api.get("/v1/reactivation/admin/stats"),
      ]);
      setRows(list.data.items || []);
      setStats(st.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <PrivateLayout title="Réactivation"><div className="afr-empty" data-testid="reactivation-admin-forbidden">
        <div className="afr-empty__title">Accès restreint</div>
      </div></PrivateLayout>
    );
  }

  return (
    <PrivateLayout
      title="Réactivation de compétence"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Réactivation" }]}
      actions={<Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>}
      data-testid="admin-reactivation-page"
    >
      {/* KPIs */}
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 16 }} data-testid="reactivation-kpis">
        <Kpi Icon={Users} label="Total utilisateurs" value={stats?.total_users_with_records || 0} accent="#0F2A42" />
        <Kpi Icon={Sparkles} label="Talents à réactiver" value={stats?.talents_wanting_reactivation || 0} accent="#15803D" testid="kpi-to-reactivate" />
        <Kpi Icon={Zap} label="Recherchent activement" value={stats?.actively_seeking || 0} accent="#009640" />
        <Kpi Icon={Eye} label="Vitrine publique activée" value={stats?.public_available_count || 0} accent="#B45309" />
      </div>

      {/* Filtres */}
      <article className="afr-card" style={{ padding: 12, marginBottom: 16 }} data-testid="reactivation-filters">
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.86rem" }}>
            <input
              type="checkbox"
              checked={filters.to_reactivate_only}
              onChange={(e) => setFilters((f) => ({ ...f, to_reactivate_only: e.target.checked }))}
              data-testid="filter-to-reactivate-only"
            />
            À réactiver uniquement
          </label>

          <Select value={filters.current_practice}
            onChange={(v) => setFilters((f) => ({ ...f, current_practice: v }))}
            testid="filter-current-practice"
            options={[
              { v: "", label: "Pratique actuelle : Toutes" },
              { v: "regular", label: "Régulière" },
              { v: "occasional", label: "Occasionnelle" },
              { v: "not_anymore", label: "Non-pratiquée" },
              { v: "wants_to_resume", label: "Souhaite reprendre" },
            ]} />

          <Select value={filters.wants_to_resume}
            onChange={(v) => setFilters((f) => ({ ...f, wants_to_resume: v }))}
            testid="filter-wants-resume"
            options={[
              { v: "", label: "Reprise : Toutes" },
              { v: "yes_active", label: "Oui, activement" },
              { v: "yes_opportunity", label: "Si opportunité" },
              { v: "maybe", label: "Peut-être" },
              { v: "no", label: "Non" },
            ]} />

          <Select value={filters.since_last_practice}
            onChange={(v) => setFilters((f) => ({ ...f, since_last_practice: v }))}
            testid="filter-since-last"
            options={[
              { v: "", label: "Interruption : Toutes" },
              { v: "lt_6m", label: "< 6 mois" },
              { v: "6m_1y", label: "6 mois – 1 an" },
              { v: "1y_2y", label: "1 – 2 ans" },
              { v: "2y_5y", label: "2 – 5 ans" },
              { v: "gt_5y", label: "> 5 ans" },
            ]} />

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
            <Search size={12} />
            <input placeholder="Recherche compétence…"
              value={filters.skill_q}
              onChange={(e) => setFilters((f) => ({ ...f, skill_q: e.target.value }))}
              data-testid="filter-skill-q"
              style={{ padding: "4px 8px", fontSize: "0.82rem", border: "1px solid var(--afr-border, #E2E8F0)", borderRadius: 6 }} />
            <button className="afr-btn afr-btn--sm afr-btn--outline" onClick={load} data-testid="reactivation-refresh">
              <RefreshCw size={12} />
            </button>
          </div>
        </div>
      </article>

      {/* Top compétences */}
      {stats?.top_skills_to_reactivate?.length > 0 && (
        <article className="afr-card" style={{ padding: 12, marginBottom: 16 }} data-testid="reactivation-top-skills">
          <h3 style={{ margin: "0 0 8px", fontSize: "0.92rem", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <TrendingUp size={14} /> Top compétences à réactiver
          </h3>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {stats.top_skills_to_reactivate.map((s) => (
              <span key={s.skill} className="afr-badge" style={{ background: "rgba(21,128,61,0.10)", color: "#15803D" }}>
                {s.skill} · <strong>{s.count}</strong>
              </span>
            ))}
          </div>
        </article>
      )}

      {loading ? (
        <div data-testid="reactivation-admin-loading">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="afr-empty" data-testid="reactivation-admin-empty">
          <div className="afr-empty__title">Aucun résultat</div>
          <div className="afr-muted">Ajustez les filtres pour élargir la recherche.</div>
        </div>
      ) : (
        <table className="afr-table" data-testid="reactivation-admin-table">
          <thead>
            <tr>
              <th>Utilisateur</th><th>Rôle</th><th>Compétences correspondantes</th>
              <th style={{ textAlign: "center" }}>Motif privé</th>
              <th>Mis à jour</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} data-testid={`reactivation-row-${u.user_id}`}>
                <td>
                  <div style={{ fontWeight: 600 }}><UserIcon size={12} style={{ verticalAlign: "middle" }} /> {u.user_name || u.user_id}</div>
                  <div className="afr-muted" style={{ fontSize: "0.76rem" }}>{u.user_email}</div>
                </td>
                <td><span className="afr-badge">{u.user_role || "—"}</span></td>
                <td>
                  <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {(u.items || []).map((it, i) => (
                      <div key={`${u.user_id}-${it.skill}-${i}`} style={{ fontSize: "0.82rem" }}>
                        <strong>{it.skill}</strong>
                        <span className="afr-muted">
                          {" · "}{LBL_PRACT[it.current_practice] || it.current_practice}
                          {it.since_last_practice && <> · Interruption {LBL_SINCE[it.since_last_practice]}</>}
                          {it.wants_to_resume && <> · Reprise : {LBL_RESUME[it.wants_to_resume]}</>}
                          {typeof it.prev_years === "number" && <> · {it.prev_years} an(s) d'expérience</>}
                          {it.public_available && <> · <span style={{ color: "#15803D" }}>Vitrine ON</span></>}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                <td style={{ textAlign: "center" }}>
                  {(u.items || []).some((it) => it.reason_present) ? (
                    <span className="afr-badge" style={{ background: "rgba(180,83,9,0.14)", color: "#B45309" }} title="Motif privé renseigné (non affiché ici)">
                      <ListChecks size={11} /> Présent
                    </span>
                  ) : "—"}
                </td>
                <td style={{ fontSize: "0.76rem" }}>{u.updated_at ? new Date(u.updated_at).toLocaleString("fr-FR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </PrivateLayout>
  );
}

const LBL_PRACT = { regular: "Régulière", occasional: "Occasionnelle", not_anymore: "Non-pratiquée", wants_to_resume: "Souhaite reprendre" };
const LBL_SINCE = { lt_6m: "<6 mois", "6m_1y": "6m–1 an", "1y_2y": "1–2 ans", "2y_5y": "2–5 ans", gt_5y: ">5 ans" };
const LBL_RESUME = { yes_active: "Activement", yes_opportunity: "Si opportunité", maybe: "Peut-être", no: "Non" };

function Select({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      style={{ padding: "4px 8px", fontSize: "0.82rem", border: "1px solid var(--afr-border, #E2E8F0)", borderRadius: 6 }}
    >
      {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
    </select>
  );
}

function Kpi({ Icon, label, value, accent, testid }) {
  return (
    <div className="afr-kpi" data-testid={testid} style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
    </div>
  );
}
